/**
 * Fastidi/Storico — fonte UNICA di verità per:
 *  - le etichette di zona corporea, tipo fastidio e impatto sull'attività;
 *  - la scala di intensità (1-5) → colore token (dark-ready) e legenda.
 *
 * Prima di questo file la stessa logica era reinventata (con hex fissi non
 * dark-ready) in più punti: `shared/ComplaintBadge/ComplaintBadge.tsx`,
 * `AthleteBodyMapHistoryView`, `BodyMapMonthCalendar`, `BodyMapTimeline`.
 * I cluster che toccano quei componenti devono migrarli su questo modulo
 * invece di mantenere la propria copia.
 */
import { BODY_ZONE_LABELS } from '../../enums/bodyZoneLabels';

/**
 * Zona corporea → etichetta leggibile.
 * Riesportata da `enums/bodyZoneLabels.ts` (fonte unica già allineata al
 * mobile, bug 8680d4). NB: `enums/injuries.ts` espone anche
 * `INJURY_BODY_ZONE_LABEL`, ma con testo leggermente divergente (es. "Torace"
 * vs "Petto", "Hamstring" vs "Ischiotibiali") — qui si resta sulla versione
 * di `bodyZoneLabels.ts` perché è quella dichiarata "fonte unica di verità".
 */
export const ZONE_LABELS: Record<string, string> = BODY_ZONE_LABELS;

/** Tipo di fastidio segnalato → etichetta leggibile. */
export const TYPE_LABELS: Record<string, string> = {
  pain: 'Dolore',
  stiffness: 'Rigidità',
  swelling: 'Gonfiore',
  weakness: 'Debolezza',
};

/** Impatto del fastidio sull'attività → etichetta leggibile. */
export const IMPACT_LABELS: Record<string, string> = {
  can_do_all: 'Nessuna limit.',
  will_limit: 'Limitazione parziale',
  cannot_do_all: 'Non riesce a fare',
};

/** Voce della legenda intensità (fascia + colore token). */
export interface IntensityLegendItem {
  label: string;
  /** CSS var pronta all'uso (es. `var(--c-warning)`), mai hex. */
  token: string;
}

/**
 * Legenda canonica delle 3 fasce di intensità (scala 1-5):
 * Lieve (1-2) · Moderata (3) · Intensa (4-5).
 */
export const INTENSITY_LEGEND: IntensityLegendItem[] = [
  { label: 'Lieve', token: 'var(--c-yellow)' },
  { label: 'Moderata', token: 'var(--c-warning)' },
  { label: 'Intensa', token: 'var(--c-error)' },
];

/**
 * Colore (CSS var token) per l'intensità 1-5 di un fastidio.
 * Lieve 1-2 → giallo brand, Moderato 3 → warning, Intenso 4-5 → error.
 * Non esiste un token `--c-warning-soft` in `_themes.scss`: per la fascia
 * "Lieve" si usa `var(--c-yellow)`, che è il colore già in uso storicamente
 * (evita la regressione light descritta nel revert 2026-04-30).
 */
export function getIntensityColor(intensity: number): string {
  if (intensity >= 4) return 'var(--c-error)';
  if (intensity === 3) return 'var(--c-warning)';
  return 'var(--c-yellow)';
}

/** Etichetta leggibile per l'intensità 1-5 (coerente con `INTENSITY_LEGEND`). */
export function getIntensityLabel(intensity: number): string {
  if (intensity >= 4) return 'Intensa';
  if (intensity === 3) return 'Moderata';
  return 'Lieve';
}

/**
 * Colore (CSS var token) per l'impatto del fastidio sull'attività:
 * nessuna limitazione → success, limitazione parziale → warning,
 * non riesce a fare l'attività → error.
 */
export function getImpactColor(impact: string): string {
  if (impact === 'cannot_do_all') return 'var(--c-error)';
  if (impact === 'will_limit') return 'var(--c-warning)';
  return 'var(--c-success)';
}
